import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Modal, { ModalProps } from '@/components/ui/Modal/Modal';
import ModalContent from '@/components/ui/Modal/ModalContent';
import LicenseForm, { LicenseFormProps } from '@/components/ui/SearchModals/LicenseForm';
import { LicenseData, validateRequiredFields } from '../SearchModals/utils/licenses';
import { UserLicense } from '@/@types/client-api';

type AddLicenseModalContent = {
  title?: string;
  subheading?: string;
  saveButtonLabel?: string;
};

export type AddLicenseModalProps = ModalProps &
  LicenseFormProps & {
    onSave: (licenseData?: LicenseData) => void;
    onBack?: () => void;
    triggerButton?: JSX.Element;
    customModalId?: string;
    isLoggedOut?: boolean;
    handleLoggedOutSave?: (license: UserLicense) => void;
    isSubmitLoading?: boolean;
    renderContentOnly?: boolean;
    shouldCloseOnSave?: boolean;
    saveButtonDisabled?: boolean;
    footerErrorMessage?: string;
    content?: AddLicenseModalContent;
  };

const AddLicenseModal = ({
  open,
  setOpen,
  data,
  onDataChange,
  creditTypeOptions,
  jurisdictionOptions,
  complianceGroupOptions,
  licenseNumberSettings,
  onSave,
  onBack,
  triggerButton,
  customModalId,
  isLoggedOut = false,
  handleLoggedOutSave,
  isSubmitLoading,
  renderContentOnly = false,
  shouldCloseOnSave = true,
  saveButtonDisabled,
  footerErrorMessage,
  content: _content
}: AddLicenseModalProps): JSX.Element => {
  const content = useMemo(
    () => ({
      title: _content?.title || 'Add a license',
      subheading: _content?.subheading || '* Indicates required field',
      saveButtonLabel: _content?.saveButtonLabel || (!isLoggedOut ? 'Add to my profile' : 'Apply')
    }),
    [_content?.saveButtonLabel, _content?.subheading, _content?.title, isLoggedOut]
  );

  const [validationFailed, setValidationFailed] = useState(false);
  const latestDataRef = useRef<LicenseData>();

  useEffect(() => {
    if (open) {
      setValidationFailed(false);
      latestDataRef.current = data;
    }
  }, [data, open]);

  const handleOnSave = useCallback(() => {
    const currentData = latestDataRef.current ?? data;
    if (saveButtonDisabled) {
      return;
    }

    if (!validateRequiredFields(currentData)) {
      setValidationFailed(true);
      return;
    }

    if (isLoggedOut) {
      handleLoggedOutSave?.({ ...currentData });
      setOpen(false);
      return;
    }

    if (shouldCloseOnSave) {
      setOpen(false);
    }
    onSave(currentData);
  }, [
    data,
    handleLoggedOutSave,
    isLoggedOut,
    onSave,
    saveButtonDisabled,
    setOpen,
    shouldCloseOnSave
  ]);

  const handleOnDataChange = useCallback(
    (data: LicenseData) => {
      latestDataRef.current = data;
      setValidationFailed(false);
      onDataChange?.(data);
    },
    [onDataChange]
  );

  const component = (
    <ModalContent
      title={content.title}
      subtitle={content.subheading}
      saveButtonLabel={content.saveButtonLabel}
      onSaveButtonPress={handleOnSave}
      isMultiStep={true}
      customModalId={customModalId}
      onBackButtonPress={onBack}
      isSubmitLoading={isSubmitLoading}
      saveButtonDisabled={saveButtonDisabled}
      footerErrorMessage={footerErrorMessage}
    >
      <div className="mt-1.5">
        <LicenseForm
          data={data}
          onDataChange={handleOnDataChange}
          creditTypeOptions={creditTypeOptions}
          jurisdictionOptions={jurisdictionOptions}
          complianceGroupOptions={!isLoggedOut ? complianceGroupOptions : undefined}
          licenseNumberSettings={!isLoggedOut ? licenseNumberSettings : undefined}
          validationFailed={validationFailed}
          isLoggedOut={isLoggedOut}
        />
      </div>
    </ModalContent>
  );

  if (renderContentOnly) {
    return component;
  }

  return (
    <Modal open={open} setOpen={setOpen} data-component="AddLicenseModal">
      {triggerButton && <Modal.Trigger asChild>{triggerButton}</Modal.Trigger>}
      {component}
    </Modal>
  );
};

export default AddLicenseModal;
